import React, { useRef, useState } from 'react';
import { FiUploadCloud, FiPaperclip } from 'react-icons/fi';
import Button from './Button';

const DEFAULT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xls,.xlsx';

export default function FileUploader({
  accept = DEFAULT_ACCEPT,
  maxSizeMb = 10,
  onSelect,
  buttonLabel = 'Attach file',
  variant = 'button', // 'button' | 'dropzone'
  disabled,
}) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');

  function validate(file) {
    if (!file) return 'No file selected';
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > maxSizeMb) return `File exceeds ${maxSizeMb}MB`;
    return null;
  }

  function handle(file) {
    setError('');
    const v = validate(file);
    if (v) {
      setError(v);
      return;
    }
    onSelect?.(file);
  }

  function onPick(e) {
    const file = e.target.files?.[0];
    handle(file);
    e.target.value = '';
  }

  function onDrop(e) {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    handle(file);
  }

  if (variant === 'button') {
    return (
      <div>
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={onPick} />
        <Button
          variant="outline"
          icon={FiPaperclip}
          disabled={disabled}
          onClick={() => ref.current?.click()}
        >
          {buttonLabel}
        </Button>
        {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={onPick} />
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center text-sm transition-colors ${
          drag ? 'border-primary bg-primary/5 text-primary' : 'border-slate-300 bg-slate-50 text-ink-muted hover:bg-slate-100'
        } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
      >
        <FiUploadCloud size={24} />
        <div>
          <div className="font-medium text-ink">Click or drag a file here</div>
          <div className="text-xs">Accepted: {accept} (max {maxSizeMb}MB)</div>
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}
